import React, { useEffect, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from "recharts";
import { format, addMinutes, isSameDay } from "date-fns";

function Charts({
  loadData = [],
  timeUnitMinutes = 60,
  barWidth = 50,
  baseLoad = null,
  maxLoad = null,
  showAboveBaseLoad = false,
}) {
  const chartRef = useRef(null);
  const formattedData = loadData.map((item) => ({
    time: new Date(item.time).getTime(),
    originalTime: item.time,
    value: item.value,
    normalValue: item.value <= (baseLoad || Infinity) ? item.value : baseLoad,
    overloadValue: item.value > (baseLoad || Infinity) ? item.value : 0,
  }));
  const getYAxisDomain = () => {
    if (showAboveBaseLoad && baseLoad !== null) {
      const maxValue = Math.max(...formattedData.map((item) => item.value));
      const yMax = Math.max(maxValue, maxLoad || maxValue) + 30;
      return [baseLoad, yMax];
    } else {
      return [0, "dataMax + 30"];
    }
  };
  const generateTicks = () => {
    if (formattedData.length === 0) return [];
    let startTime, endTime;
    startTime = new Date(loadData[0].time);
    endTime = new Date(loadData[loadData.length - 1].time);
    startTime.setMinutes(
      Math.floor(startTime.getMinutes() / timeUnitMinutes) * timeUnitMinutes
    );
    startTime.setSeconds(0);
    const ticks = [];
    let currentTime = new Date(startTime);
    while (currentTime <= endTime) {
      ticks.push(currentTime.getTime());
      currentTime = addMinutes(currentTime, timeUnitMinutes);
    }
    return ticks;
  };
  const ticks = generateTicks();
  // const getTimeFormat = () => {
  //   return timeUnitMinutes >= 60 ? "MM/dd HH:00" : "MM/dd HH:mm";
  // };
  const CustomXAxisTick = (props) => {
    const { x, y, payload, index, ticks } = props;
    const date = new Date(payload.value);
    const timeStr = format(date, timeUnitMinutes >= 60 ? "HH:00" : "HH:mm");
    const dateStr = format(date, "MM/dd");
    const showDate =
      index === 0 || !isSameDay(date, new Date(ticks[index - 1]));
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={10}>
          {timeStr}
        </text>
        {showDate && (
          <text
            x={0}
            y={16}
            dy={16}
            textAnchor="middle"
            fill="#999"
            fontSize={10}
          >
            {dateStr}
          </text>
        )}
      </g>
    );
  };
  const xAxisDomain = ["dataMin", "dataMax"];
  const chartKey = `${showAboveBaseLoad}-${baseLoad}-${maxLoad}-${loadData.length}`;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        key={chartKey}
        ref={chartRef}
        data={formattedData}
        margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="10 10" />
        <XAxis
          dataKey="time"
          type="number"
          domain={xAxisDomain}
          scale="time"
          ticks={ticks}
          height={50}
          angle={0}
          interval={0}
          padding={{ left: barWidth / 2, right: barWidth / 2 }}
          tickSize={8}
          tick={<CustomXAxisTick ticks={ticks} />}
        />
        <YAxis domain={getYAxisDomain()} key={`yaxis-${chartKey}`} />
        <Tooltip
          labelFormatter={(timestamp) =>
            format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")
          }
          formatter={(_, __, props) => {
            const originalValue = props.payload.value;
            return [`${originalValue}%`, "부하율"];
          }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const originalValue = payload[0].payload.value;
              return (
                <div
                  style={{
                    backgroundColor: "#fff",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                >
                  <p style={{ margin: 0 }}>
                    {format(new Date(label), "yyyy-MM-dd HH:mm:ss")}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color:
                        originalValue > (maxLoad || Infinity)
                          ? "#e74c3c"
                          : "#1f8687",
                    }}
                  >
                    <span>부하율: {originalValue}%</span>
                  </p>
                </div>
              );
            }
            return null;
          }}
        />
        {baseLoad && (
          <ReferenceLine
            y={baseLoad}
            stroke="#2ecc71"
            strokeWidth={2}
            strokeDasharray="3 3"
          >
            <Label
              position="top"
              offset={10}
              fill="#2ecc71"
              fontSize={12}
              fontWeight="bold"
            />
          </ReferenceLine>
        )}
        {maxLoad && (
          <ReferenceLine
            y={maxLoad}
            stroke="#e74c3c"
            strokeWidth={2}
            strokeDasharray="3 3"
          >
            <Label
              position="top"
              offset={10}
              fill="#e74c3c"
              fontSize={12}
              fontWeight="bold"
            />
          </ReferenceLine>
        )}
        <Area
          type="stepAfter"
          dataKey="normalValue"
          stroke="#ffffff"
          fill="rgba(171, 188, 255, 0.5)"
          name="부하율"
          isAnimationActive={false}
          activeDot={{
            r: 5,
            stroke: "#000",
            strokeWidth: 1,
            fill: "#fff",
            strokeDasharray: "",
            onClick: () => {},
          }}
        />
        <Area
          type="stepAfter"
          dataKey="overloadValue"
          stroke="#ffffff"
          fill="rgba(255, 36, 36, 0.5)"
          name="과부하"
          isAnimationActive={false}
          stackId="1"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default Charts;
